import { useEffect, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'

import {
  getGetAdminCalendarQueryKey,
  getGetBookingHistoryQueryKey,
  useChangeBookingHeadcount,
  useGetAdminCalendar,
  useGetAdminQuote,
  useGetBookingHistory,
  useGrantCredit,
  useMarkBookingPaid,
  useMarkNoShow,
  useRestoreBooking,
  useUnmarkBookingPaid,
} from '../../../api/generated/endpoints/admin/admin'
import type { CalendarBooking } from '../../../api/generated/model'
import { useCancelBooking } from '../../../api/generated/endpoints/bookings/bookings'
import { useListRates } from '../../../api/generated/endpoints/rates/rates'
import { ActivityRow } from '../../../components/ActivityList'
import { KIND_LABELS, SIN_NOMBRE, toMinutes } from '../../../lib/admin-calendar'
import { apiMessage } from '../../../lib/api-error'
import { formatUYU } from '../../../lib/money'
import { addDays, formatDayLong, todayAtFacility } from '../../../lib/time'

/**
 * One booking, and everything that can be done to it.
 *
 * A screen with a title and a back button, reached by tapping a card. That is
 * not a nested menu -- it is the one navigation idiom nobody has ever been
 * confused by, which is why the busy screens carry at most one inline action
 * and send everything else here.
 */
export const Route = createFileRoute('/_authed/_admin/admin/booking/$bookingId')({
  component: BookingDetailPage,
})

function BookingDetailPage() {
  const { bookingId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const today = todayAtFacility()

  // Read from the same calendar cache the other screens use, over a wide
  // window so a booking reached from any day is found.
  const calendar = useGetAdminCalendar({ from: addDays(today, -30), to: addDays(today, 60) })
  // The history too: every action on this screen adds a line to it.
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getGetAdminCalendarQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getGetBookingHistoryQueryKey(bookingId) }),
    ])

  const cancel = useCancelBooking({ mutation: { onSuccess: invalidate } })
  const restore = useRestoreBooking({ mutation: { onSuccess: invalidate } })
  const noShow = useMarkNoShow({ mutation: { onSuccess: invalidate } })
  const grant = useGrantCredit({ mutation: { onSuccess: invalidate } })

  const booking = calendar.data?.bookings.find((candidate) => candidate.id === bookingId)

  // Remember it. The calendar deliberately stops returning a cancelled booking
  // -- it no longer occupies a bay -- but the undo bar still has to be able to
  // say whose booking it was.
  const [snapshot, setSnapshot] = useState<typeof booking>(undefined)
  useEffect(() => {
    if (booking) setSnapshot(booking)
  }, [booking])

  const wasCancelled = cancel.isSuccess && !restore.isSuccess
  const shown = booking ?? snapshot

  if (calendar.isPending && !shown) return <p className="text-xl">Cargando…</p>

  if (!shown) {
    return (
      <div className="space-y-4">
        <p className="text-xl">Esa reserva ya no está en el calendario.</p>
        <Link to="/admin/today" className="text-lg underline">
          Volver a hoy
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      <Link
        to="/admin/calendar"
        search={{ date: shown.date }}
        className="inline-flex min-h-14 items-center text-lg underline"
      >
        &lsaquo;&nbsp; Volver al calendario
      </Link>

      <div className="rounded-lg border-2 border-slate-300 bg-white p-6">
        <p className="text-sm font-semibold uppercase">{KIND_LABELS[shown.kind] ?? shown.kind}</p>
        <p className="mt-1 text-3xl font-semibold tabular-nums">
          {shown.start} &rarr; {shown.end}
        </p>
        <p className="text-xl">{formatDayLong(shown.date)}</p>
        <p className="mt-3 text-xl font-medium">
          {shown.customer_name ?? shown.admin_note ?? SIN_NOMBRE}
        </p>
        {shown.coach_name && <p className="text-lg text-slate-700">con {shown.coach_name}</p>}
        {shown.customer_phone && (
          <a
            href={`tel:${shown.customer_phone}`}
            className="inline-flex min-h-11 items-center text-lg tabular-nums underline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          >
            {shown.customer_phone}
          </a>
        )}
        <p className="mt-3 text-lg">
          {shown.bay_name}
          {shown.price_uyu_pesos > 0 && ` · ${formatUYU(shown.price_uyu_pesos)}`}
        </p>
        {shown.rate_name && (
          <p className="text-lg text-slate-700">
            Tarifa «{shown.rate_name}» · {shown.headcount ?? 1}{' '}
            {(shown.headcount ?? 1) === 1 ? 'persona' : 'personas'}
          </p>
        )}
      </div>

      {/* Undo rather than a confirmation dialog. A dialog is a trap for
          somebody who reads slowly, and it trains reflexive dismissal, which
          means it stops protecting anything. This bar is sticky rather than
          floating, so it never covers what he is reading, and it does not
          vanish on a timer. */}
      {wasCancelled ? (
        <div
          role="status"
          className="sticky bottom-0 flex flex-wrap items-center justify-between gap-4 border-t-4 border-slate-900 bg-white px-6 py-5"
        >
          <p className="text-xl">
            Se canceló <span className="font-semibold">{shown.customer_name ?? 'esa reserva'}</span>
            , {shown.start}.
          </p>
          <button
            type="button"
            onClick={() => restore.mutate({ bookingId })}
            disabled={restore.isPending}
            className="min-h-16 rounded-md bg-slate-900 px-8 text-xl text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:bg-slate-400"
          >
            {restore.isPending ? 'Deshaciendo…' : 'Deshacer'}
          </button>
          {restore.isError && (
            <p role="alert" className="w-full text-lg text-red-700">
              {apiMessage(restore.error, 'No se pudo deshacer.')}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {shown.customer_id && (
            <button
              type="button"
              // shown.id is the BOOKING. Granting an hour is something done to
              // a person, and passing the wrong id here meant this button had
              // never once worked.
              onClick={() =>
                shown.customer_id &&
                grant.mutate({ userId: shown.customer_id, params: { hours: 1 } })
              }
              disabled={grant.isPending}
              className="min-h-16 w-full rounded-md border-2 border-slate-400 bg-white px-6 text-xl focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
            >
              {grant.isPending ? 'Acreditando…' : 'Acreditar una hora'}
            </button>
          )}

          {shown.rate_id !== null &&
            shown.rate_id !== undefined &&
            (shown.kind === 'practice' || shown.kind === 'class') &&
            shown.status !== 'cancelled' &&
            shown.status !== 'expired' && <PeoplePanel booking={shown} onChanged={invalidate} />}

          {shown.price_uyu_pesos > 0 && shown.kind !== 'block' && (
            <SettlementPanel booking={shown} onChanged={invalidate} />
          )}

          {/* No time limit, deliberately. HOY only offers this while the hour
              is running, so if it were bounded here too an absence noticed
              afterwards could never be recorded -- and once past sessions are
              completed automatically, never at all. The service accepts
              anything that was not cancelled. */}
          {(shown.status === 'confirmed' || shown.status === 'completed') && (
            <button
              type="button"
              onClick={() => noShow.mutate({ bookingId })}
              disabled={noShow.isPending}
              className="min-h-16 w-full rounded-md border-2 border-slate-400 bg-white px-6 text-xl focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
            >
              {noShow.isPending ? 'Marcando…' : 'No se presentó'}
            </button>
          )}
          {noShow.isError && (
            <p role="alert" className="text-lg text-red-700">
              {apiMessage(noShow.error, 'No se pudo marcar la ausencia.')}
            </p>
          )}

          <button
            type="button"
            onClick={() => cancel.mutate({ bookingId, data: { reason: null, note: null } })}
            disabled={cancel.isPending}
            className="min-h-16 w-full rounded-md bg-red-700 px-6 text-xl text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:bg-slate-400"
          >
            {cancel.isPending ? 'Cancelando…' : 'Cancelar esta reserva'}
          </button>

          {cancel.isError && (
            <p role="alert" className="text-lg text-red-700">
              {apiMessage(cancel.error, 'No se pudo cancelar la reserva.')}
            </p>
          )}
        </div>
      )}

      <History bookingId={shown.id} />

      <button
        type="button"
        onClick={() => void navigate({ to: '/admin/today' })}
        className="inline-flex min-h-11 items-center text-lg underline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
      >
        Volver a hoy
      </button>
    </div>
  )
}

/**
 * What the coach was handed, and how.
 *
 * Two buttons rather than a method dropdown: picking from a list is a decision,
 * and both answers are one tap. The amount only appears when he says it
 * differed -- the booking already knows what was owed, and asking for a number
 * every time would be asking him to retype the price.
 *
 * Correcting a mistake is undo-then-mark rather than an edit, so both facts
 * stay in the audit log instead of one quietly replacing the other.
 */
function SettlementPanel({
  booking,
  onChanged,
}: {
  booking: CalendarBooking
  onChanged: () => void
}) {
  const [otherAmount, setOtherAmount] = useState('')
  const [showAmount, setShowAmount] = useState(false)

  const markPaid = useMarkBookingPaid({ mutation: { onSuccess: onChanged } })
  const unmark = useUnmarkBookingPaid({ mutation: { onSuccess: onChanged } })

  const owed = formatUYU(booking.price_uyu_pesos)

  // Settled, but not with him: an hour off a package, or an online payment from
  // back when those were on. Nothing to collect and nothing to undo here -- the
  // credit ledger owns that fact -- but saying so is what stops him wondering
  // why there is no button.
  if (booking.settled && !booking.paid) {
    return (
      <div className="rounded-lg border-2 border-slate-300 bg-white p-6">
        <p className="text-xl">Ya estaba paga. No hay nada para cobrar.</p>
      </div>
    )
  }

  if (booking.paid) {
    return (
      <div className="rounded-lg border-2 border-green-700 bg-green-50 p-6">
        <p className="text-xl font-medium text-green-900">
          Cobrado {formatUYU(booking.paid_amount_uyu_pesos ?? booking.price_uyu_pesos)}
          {booking.payment_method ? ` en ${booking.payment_method}` : ''}
        </p>
        {booking.paid_amount_uyu_pesos != null &&
          booking.paid_amount_uyu_pesos !== booking.price_uyu_pesos && (
            <p className="mt-1 text-lg text-green-900">El precio era {owed}.</p>
          )}
        <button
          type="button"
          onClick={() => unmark.mutate({ bookingId: booking.id })}
          disabled={unmark.isPending}
          className="mt-4 min-h-16 w-full rounded-md border-2 border-slate-400 bg-white px-6 text-xl focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          {unmark.isPending ? 'Deshaciendo…' : 'Deshacer el cobro'}
        </button>
        {unmark.isError && (
          <p role="alert" className="mt-2 text-lg text-red-700">
            {apiMessage(unmark.error, 'No se pudo deshacer el cobro.')}
          </p>
        )}
      </div>
    )
  }

  // Empty means "the price stands", which is what the API expects as null.
  const amount = otherAmount.trim() === '' ? null : Number(otherAmount)
  const amountIsUsable = amount === null || (Number.isInteger(amount) && amount >= 0)
  const pay = (method: 'efectivo' | 'transferencia') =>
    markPaid.mutate({ bookingId: booking.id, data: { method, amount_uyu_pesos: amount } })

  return (
    <div className="rounded-lg border-2 border-amber-500 bg-amber-50 p-6">
      <p className="text-xl font-medium text-amber-900">Debe {owed}</p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => pay('efectivo')}
          disabled={markPaid.isPending || !amountIsUsable}
          className="min-h-16 rounded-md bg-slate-900 px-6 text-xl text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:bg-slate-400"
        >
          Cobré en efectivo
        </button>
        <button
          type="button"
          onClick={() => pay('transferencia')}
          disabled={markPaid.isPending || !amountIsUsable}
          className="min-h-16 rounded-md bg-slate-900 px-6 text-xl text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:bg-slate-400"
        >
          Cobré por transferencia
        </button>
      </div>

      {showAmount ? (
        <div className="mt-4">
          <label htmlFor="otro-importe" className="block text-lg font-medium">
            Importe cobrado
          </label>
          <p className="mt-0.5 text-base text-amber-900">
            En blanco queda el precio de la reserva, {owed}.
          </p>
          <input
            id="otro-importe"
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={otherAmount}
            onChange={(event) => setOtherAmount(event.target.value)}
            className="mt-2 min-h-16 w-full rounded-md border-2 border-slate-400 px-4 text-xl tabular-nums focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowAmount(true)}
          className="mt-4 inline-flex min-h-14 items-center text-lg underline"
        >
          Cobré otro importe
        </button>
      )}

      {markPaid.isError && (
        <p role="alert" className="mt-2 text-lg text-red-700">
          {apiMessage(markPaid.error, 'No se pudo registrar el cobro.')}
        </p>
      )}
    </div>
  )
}

/**
 * Booked for one, turned up with three. Decision 0005.
 *
 * The headcount picks the line -- three on the sim is "2 a 4", not "1
 * persona" -- so changing it may move the booking to another line, and the
 * new price is QUOTED before he commits, never worked out here. The length
 * stays: it is bay time already held, so only lines that fit it are offered.
 *
 * Once paid, it is shown and not offered. The payment recorded what was
 * handed over for this price; the server refuses to reprice under it, and a
 * button that always fails is worse than a sentence saying why.
 */
function PeoplePanel({
  booking,
  onChanged,
}: {
  booking: CalendarBooking
  onChanged: () => unknown
}) {
  // Optional in the generated type because the schema defaults it.
  const current = booking.headcount ?? 1
  const rates = useListRates({ query: { staleTime: 60_000 } })
  const minutes = toMinutes(booking.end) - toMinutes(booking.start)
  const withCoach = booking.kind === 'class'
  const lines = (rates.data?.rates ?? []).filter(
    (rate) =>
      rate.with_coach === withCoach &&
      rate.prices.some((p) => p.bay_id === booking.bay_id) &&
      (rate.pricing === 'round' ? rate.round_minutes === minutes : minutes % 60 === 0),
  )
  const most = Math.max(current, ...lines.map((rate) => rate.max_people))

  const [count, setCount] = useState(current)
  const fitting = lines.filter((r) => r.min_people <= count && count <= r.max_people)
  const rate = fitting.find((r) => r.id === booking.rate_id) ?? fitting[0] ?? null
  const changing = count !== current

  const quote = useGetAdminQuote(
    {
      bay_id: booking.bay_id,
      rate_id: rate?.id ?? 0,
      headcount: count,
      hours: Math.max(1, Math.round(minutes / 60)),
    },
    { query: { enabled: changing && rate !== null, retry: false } },
  )
  const change = useChangeBookingHeadcount({
    mutation: { retry: false, onSuccess: () => onChanged() },
  })

  if (lines.length === 0 && !rates.isPending) return null

  return (
    <section className="rounded-lg border-2 border-slate-300 bg-white p-5">
      <h2 className="text-xl font-semibold">Personas</h2>

      {booking.paid ? (
        <p className="mt-2 text-lg text-slate-700">
          Ya está cobrada: para cambiar las personas, primero hay que deshacer el cobro.
        </p>
      ) : (
        <>
          <fieldset className="mt-3">
            <legend className="sr-only">¿Cuántas personas vinieron?</legend>
            <div className="grid grid-cols-4 gap-3">
              {Array.from({ length: most }, (_, i) => i + 1).map((n) => (
                <label key={n}>
                  <input
                    type="radio"
                    name="headcount"
                    value={n}
                    checked={count === n}
                    onChange={() => setCount(n)}
                    className="peer sr-only"
                  />
                  <span className="flex min-h-14 cursor-pointer items-center justify-center rounded-lg border-2 border-slate-400 bg-white text-xl peer-not-checked:hover:bg-slate-50 peer-checked:border-slate-900 peer-checked:bg-slate-900 peer-checked:text-white peer-focus-visible:outline-4 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-slate-900">
                    {n}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {changing && !rate && (
            <p className="mt-3 text-lg text-amber-900">
              Ninguna tarifa de este simulador es para {count} personas con este largo.
            </p>
          )}
          {changing && quote.data && (
            <div className="mt-4 space-y-3">
              <p className="text-lg">
                {quote.data.rate_name !== booking.rate_name && (
                  <>Pasa a «{quote.data.rate_name}». </>
                )}
                Queda en{' '}
                <span className="font-semibold">{formatUYU(quote.data.price_uyu_pesos)}</span>,
                antes {formatUYU(booking.price_uyu_pesos)}.
              </p>
              <button
                type="button"
                disabled={change.isPending}
                onClick={() =>
                  change.mutate({
                    bookingId: booking.id,
                    data: { headcount: count, rate_id: quote.data.rate_id },
                  })
                }
                className="min-h-16 w-full rounded-md bg-slate-900 px-6 text-xl text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:bg-slate-400"
              >
                {change.isPending
                  ? 'Guardando…'
                  : `Cambiar a ${count} ${count === 1 ? 'persona' : 'personas'}`}
              </button>
            </div>
          )}
          {(change.isError || quote.isError) && (
            <p role="alert" className="mt-3 text-lg text-red-700">
              {apiMessage(
                (change.error ?? quote.error) as unknown,
                'No se pudo cambiar la cantidad de personas.',
              )}
            </p>
          )}
        </>
      )}
    </section>
  )
}

/**
 * Who made this booking and who has touched it since, oldest first. The
 * owner's question -- "who blocked this, and when?" -- answered where it is
 * asked, rather than in the database console. Refetched whenever the
 * calendar is, so an action taken above shows up here at once.
 */
function History({ bookingId }: { bookingId: string }) {
  const history = useGetBookingHistory(bookingId, { query: { staleTime: 0 } })
  const entries = history.data?.entries ?? []
  if (history.isPending || entries.length === 0) return null
  return (
    <section className="rounded-lg border border-slate-300 bg-white px-5 py-4">
      <h2 className="text-xl font-semibold">Historial</h2>
      <ul className="mt-1 divide-y divide-slate-200">
        {entries.map((entry) => (
          <ActivityRow key={entry.id} entry={entry} showBooking={false} clockOnly={false} />
        ))}
      </ul>
    </section>
  )
}
